import {
  useChargingFeeDayTypeEnum,
  useChargingFeeDiscountTargetEnum,
  useChargingFeeDiscountTypeEnum,
} from '@services/chargingFee'
import { useAllSessions } from '@services/session'
import dayjs from 'dayjs'
import moment from 'moment'
import { groupBy, isNil, toPairs } from 'ramda'
import React from 'react'
import {
  ParkingFeeRateTypeEnum,
  useParkingFeeRateTypeEnum,
} from './parkingFee.enum'
import {
  IParkingFeeArea,
  IParkingFeeBasic,
  IParkingFeeDiscount,
} from './parkingFee.type'

export const useConvertParkingFeeBasic = () => {
  const { byId } = useAllSessions()

  const converter = React.useCallback(
    (data: IParkingFeeBasic[]) => {
      return data.map(item => {
        return {
          ...item,
          sessionName: byId[item.sessionId]?.name,
        }
      })
    },
    [byId]
  )

  return converter
}

export const useConvertToParkingFee = () => {
  const { byId, isFetched } = useAllSessions()
  const { byId: byRateTypeId } = useParkingFeeRateTypeEnum()
  const { byId: byIdDayType } = useChargingFeeDayTypeEnum()

  const converter = React.useCallback(
    (data: any) => {
      if (!data) {
        return null
      }
      if (!byId) {
        return null
      }

      const _convertData = (data: IParkingFeeArea) => {
        const parkingLotSession = byId[data.sessionId]
        const areaSession = byId[parkingLotSession?.parentSessionId]
        const citySession = byId[areaSession?.parentSessionId]
        let ruleList: any = []
        let progressiveFormList: {
          mainRule: any
          ruleList: any[]
        }[] = []

        if (data.rateType === ParkingFeeRateTypeEnum.Progress) {
          toPairs(groupBy(x => x.seqGroup + '', data?.ruleList || [])).forEach(
            ([group, ruleList]) => {
              progressiveFormList.push({
                mainRule: {},
                ruleList: [],
              })
              ruleList.forEach((item: any) => {
                item['dayTypeStr'] = byIdDayType[item.dayType]?.label
                item['timeRange'] = []
                item['timeList']?.forEach((x: any) => {
                  x['timeStartStr'] = dayjs()
                    .startOf('day')
                    .add(x.timeStart, 'milliseconds')
                    .format('HH:mm')
                  x['timeEndStr'] = dayjs()
                    .startOf('day')
                    .add(x.timeEnd, 'milliseconds')
                    .format('HH:mm')
                  item['timeRange'].push(
                    `${x['timeStartStr']}-${x['timeEndStr']}`
                  )
                })
                if (!isNil(item.dayType)) {
                  progressiveFormList[progressiveFormList.length - 1].mainRule =
                    item
                } else {
                  progressiveFormList[
                    progressiveFormList.length - 1
                  ].ruleList.push(item)
                }
              })
            }
          )
        } else {
          ruleList = data?.ruleList?.map((item: any) => {
            item['dayTypeStr'] = byIdDayType[item.dayType]?.label
            item['timeRange'] = []
            item['timeList']?.forEach((x: any) => {
              x['timeStartStr'] = dayjs()
                .startOf('day')
                .add(x.timeStart, 'milliseconds')
                .format('HH:mm')
              x['timeEndStr'] = dayjs()
                .startOf('day')
                .add(x.timeEnd, 'milliseconds')
                .format('HH:mm')
              item['timeRange'].push(`${x['timeStartStr']}-${x['timeEndStr']}`)
            })
            return item
          })
        }

        return {
          ...data,
          ruleList,
          progressiveFormList,
          rateTypeStr: byRateTypeId[data.rateType]?.label,
          cityName: citySession?.name,
          areaName: areaSession?.name,
          parkingLotName: parkingLotSession?.name,
        }
      }

      return {
        ..._convertData(data),
        nextParkingFee: !!data.nextParkingFee
          ? _convertData(data.nextParkingFee)
          : null,
        prevFee: !!data.nextParkingFee ? _convertData(data) : null,
      }
    },
    [byId]
  )

  return converter
}

export const useConvertToParkingFeeDiscount = () => {
  const { byId, isFetched } = useAllSessions()
  const { byId: byRateTypeId } = useParkingFeeRateTypeEnum()
  const { byId: byIdDayType } = useChargingFeeDayTypeEnum()
  const { byId: byIdChargingFeeDiscountType } = useChargingFeeDiscountTypeEnum()
  const { byId: byIdChargingFeeDiscountTarget } =
    useChargingFeeDiscountTargetEnum()

  const converter = React.useCallback(
    (data: any) => {
      if (!data) {
        return null
      }
      if (!byId) {
        return null
      }

      const _convertData = (data: IParkingFeeDiscount) => {
        let citySession = null

        if (data.sessionId?.length > 0) {
          const parkingLotSession = byId[data.sessionId[0]]
          const areaSession = byId[parkingLotSession?.parentSessionId]
          citySession = byId[areaSession?.parentSessionId]
        }
        let ruleList: any = []
        let progressiveFormList: {
          mainRule: any
          ruleList: any[]
        }[] = []

        if (data.rateType === ParkingFeeRateTypeEnum.Progress) {
          toPairs(groupBy(x => x.seqGroup + '', data?.ruleList || [])).forEach(
            ([group, ruleList]) => {
              progressiveFormList.push({
                mainRule: {},
                ruleList: [],
              })
              ruleList.forEach((item: any) => {
                item['dayTypeStr'] = byIdDayType[item.dayType]?.label
                item['timeRange'] = []
                item['timeList']?.forEach((x: any) => {
                  x['timeStartStr'] = dayjs()
                    .startOf('day')
                    .add(x.timeStart, 'milliseconds')
                    .format('HH:mm')
                  x['timeEndStr'] = dayjs()
                    .startOf('day')
                    .add(x.timeEnd, 'milliseconds')
                    .format('HH:mm')
                  item['timeRange'].push(
                    `${x['timeStartStr']}-${x['timeEndStr']}`
                  )
                })
                if (!isNil(item.dayType)) {
                  progressiveFormList[progressiveFormList.length - 1].mainRule =
                    item
                } else {
                  progressiveFormList[
                    progressiveFormList.length - 1
                  ].ruleList.push(item)
                }
              })
            }
          )
        } else {
          ruleList = data?.ruleList?.map((item: any) => {
            item['dayTypeStr'] = byIdDayType[item.dayType]?.label
            item['timeRange'] = []
            item['timeList']?.forEach((x: any) => {
              x['timeStartStr'] = dayjs()
                .startOf('day')
                .add(x.timeStart, 'milliseconds')
                .format('HH:mm')
              x['timeEndStr'] = dayjs()
                .startOf('day')
                .add(x.timeEnd, 'milliseconds')
                .format('HH:mm')
              item['timeRange'].push(`${x['timeStartStr']}-${x['timeEndStr']}`)
            })
            return item
          })
        }

        return {
          ...data,
          ruleList,
          progressiveFormList,
          datesStr:
            data?.startAt && data?.endAt
              ? `${dayjs(data?.startAt).format('YYYY/MM/DD')} ~ ${dayjs(
                  data?.endAt
                ).format('YYYY/MM/DD')}`
              : '',
          dates:
            data?.startAt && data?.endAt
              ? [moment(data?.startAt), moment(data?.endAt)]
              : [undefined, undefined],
          rateTypeStr: byRateTypeId[data.rateType]?.label,
          cityName: citySession?.name,
          cityId: citySession?.id,
          discountTypeStr:
            byIdChargingFeeDiscountType[data?.discountType]?.label,
          targetStrs: data?.target?.map(
            (x: any) => byIdChargingFeeDiscountTarget[x]?.label
          ),
        }
      }

      return {
        ..._convertData(data),
        nextParkingFee: !!data.nextParkingFee
          ? _convertData(data.nextParkingFee)
          : null,
        prevFee: !!data.nextParkingFee ? _convertData(data) : null,
      }
    },
    [byId]
  )

  return converter
}
